import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { uploadBannerAction } from '../../../../store/companyPromotions';
import { uploadFile } from '../../../../api/companyPromotions';
import { useFormValidation } from '../../hooks/useFormValidation';

// Импорт новых компонентов
import { PromotionBasicInfo } from '../PromotionBasicInfo';
import { PromotionShortDescription } from '../PromotionShortDescription';
import { PromotionBanners } from '../PromotionBanners';
import { PromotionDescription } from '../PromotionDescription';
import { PromotionSchedule } from '../PromotionSchedule';
import { PromotionButtons } from '../PromotionButtons';
import { PromotionAvailability } from '../PromotionAvailability';
import { PromotionInitiator } from '../PromotionInitiator';
import { PromotionActions } from '../PromotionActions';

export const CompanyPromotionForm = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  loading,
  error,
  cities,
  initiators,
  buttons,
  onButtonsUpdate,
  isEdit = false
}) => {
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showCreateButtonModal, setShowCreateButtonModal] = useState(false);
  
  // Хук для управления валидацией
  const {
    buttonErrors,
    dateErrors,
    formErrors,
    validateButtonLink,
    validateFormDates,
    validateFullForm,
    removeButtonErrors,
    addButtonError
  } = useFormValidation(isEdit ? 'edit' : 'create');

  // Обработчик изменения полей формы
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Валидация дат при изменении date_start или date_end
      if (field === 'date_start' || field === 'date_end') {
        validateFormDates(
          field === 'date_start' ? value : newData.date_start,
          field === 'date_end' ? value : newData.date_end
        );
      }
      
      return newData;
    });
  }, [setFormData, validateFormDates]);

  // Обработчик изменения вложенных полей
  const handleNestedInputChange = useCallback((parentField, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
  }, [setFormData]);

  // Обработчик загрузки файлов (баннеры)
  const handleFileUpload = async (field, file) => {
    if (!file) {
      setFormData(prev => ({
        ...prev,
        [field]: null
      }));
      return;
    }

    try {
      const lang = field.split('_')[1]; // Извлекаем язык из поля (banner_ru -> ru)
      const result = await dispatch(uploadBannerAction(file, lang));
      
      if (result?.krakend_url) {
        setFormData(prev => ({
          ...prev,
          [field]: result.krakend_url
        }));
      }
    } catch (error) {
      console.error('Error uploading banner:', error);
    }
  };

  // Обработчик изменения кнопок
  const handleButtonChange = (index, field, value) => {
    const newButtons = [...formData.buttons];
    
    if (field === 'button_id' || field === 'is_external_link') {
      newButtons[index] = {
        ...newButtons[index],
        [field]: value?.value || null
      };
    } else {
      newButtons[index] = {
        ...newButtons[index],
        [field]: value
      };
    }
    
    setFormData(prev => ({
      ...prev,
      buttons: newButtons
    }));

    // Валидация ссылки при изменении
    if (field === 'link' || field === 'is_external_link') {
      const button = newButtons[index];
      validateButtonLink(index, button.link, button.is_external_link);
    }
  };

  // Добавление новой кнопки
  const addButton = () => {
    const newButtons = [...formData.buttons, {
      button_id: null,
      link: '',
      is_external_link: false
    }];
    
    setFormData(prev => ({
      ...prev,
      buttons: newButtons
    }));

    // Добавляем ошибку валидации для новой кнопки
    addButtonError(newButtons.length - 1);
  };

  // Удаление кнопки
  const removeButton = (index) => {
    const newButtons = formData.buttons.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      buttons: newButtons
    }));

    // Удаляем ошибки валидации для удаленной кнопки
    removeButtonErrors(index);
  };

  // Обработчик загрузки файлов Excel
  const handleExcelFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploadedFiles(prev => [...prev, {
        name: file.name,
        status: 'uploading'
      }]);

      const data = new FormData();
      data.append('file', file);

      const result = await uploadFile(data);
      if (result?.data?.id) {
        setFormData(prev => ({
          ...prev,
          file_ids: [...(prev.file_ids || []), result.data.id]
        }));

        setUploadedFiles(prev => 
          prev.map(f => 
            f.name === file.name 
              ? { ...f, status: 'success', id: result.data.id }
              : f
          )
        );
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadedFiles(prev => 
        prev.map(f => 
          f.name === file.name 
            ? { ...f, status: 'error' }
            : f
        )
      );
    }
  };

  // Удаление загруженного файла
  const removeUploadedFile = (index) => {
    const fileToRemove = uploadedFiles[index];
    
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    
    if (fileToRemove.id) {
      setFormData(prev => ({
        ...prev,
        file_ids: prev.file_ids.filter(id => id !== fileToRemove.id)
      }));
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Полная валидация формы
    const isValid = validateFullForm(formData);
    
    // Если есть ошибки валидации, не отправляем форму
    if (!isValid) {
      return;
    }
    
    onSubmit(formData);
  };

  // Опции для городов
  const citiesOptions = cities?.filials?.map(city => ({
    label: city.city_name,
    value: city.filial_id
  })) || [];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 1200, mx: 'auto' }}>

      {/* Основная информация */}
      <PromotionBasicInfo
        formData={formData}
        onInputChange={handleInputChange}
        errors={formErrors}
      />

      {/* Краткое описание */}
      <PromotionShortDescription
        formData={formData}
        onInputChange={handleInputChange}
        errors={formErrors}
      />

      {/* Баннеры */}
      <PromotionBanners
        formData={formData}
        onFileUpload={handleFileUpload}
        errors={formErrors}
      />

      {/* Полное описание */}
      <PromotionDescription
        formData={formData}
        onInputChange={handleInputChange}
        errors={formErrors}
      />

      {/* Сроки проведения */}
      <PromotionSchedule
        formData={formData}
        onInputChange={handleInputChange}
        dateErrors={dateErrors}
      />

      {/* Настройки кнопок */}
      <PromotionButtons
        formData={formData}
        onInputChange={handleInputChange}
        onButtonChange={handleButtonChange}
        onAddButton={addButton}
        onRemoveButton={removeButton}
        buttons={buttons}
        buttonErrors={buttonErrors}
        showCreateButtonModal={showCreateButtonModal}
        setShowCreateButtonModal={setShowCreateButtonModal}
        onButtonsUpdate={onButtonsUpdate}
      />

      {/* Настройки доступности */}
      <PromotionAvailability
        formData={formData}
        onInputChange={handleInputChange}
        citiesOptions={citiesOptions}
        uploadedFiles={uploadedFiles}
        onFileUpload={handleExcelFileUpload}
        onRemoveFile={removeUploadedFile}
        formErrors={formErrors}
      />

      {/* Инициатор */}
      <PromotionInitiator
        formData={formData}
        onInputChange={handleInputChange}
        onNestedInputChange={handleNestedInputChange}
        initiators={initiators}
        formErrors={formErrors}
      />

      {/* Кнопки действий */}
      <PromotionActions
        onSubmit={handleSubmit}
        onCancel={onCancel}
        loading={loading}
        error={error}
        isEdit={isEdit}
      />
    </Box>
  );
};

CompanyPromotionForm.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  cities: PropTypes.array,
  initiators: PropTypes.array,
  buttons: PropTypes.array,
  onButtonsUpdate: PropTypes.func,
  isEdit: PropTypes.bool
};